import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons'

import {
  Box,
  Button,
  Form,
  Image,
  ModalContext,
  SidebarModal,
  StatusBadge,
  Text,
} from '@domain-group/suit-theme'

import { digObject, toggleArray } from '@domain-group/javascript-utils'

import type { TemplatePageModel } from '@models/types'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useAgencySelector from '@hooks/useAgencySelector'
import useTemplatePage from '@hooks/useTemplatePage'

import placeholderImageUrl from
  '@sections/Client/packs/AgencyAdmin/screens/TemplateGallery/screens/List/assets/placeholder.svg'

import Providers from './components/Provider'
import ServiceProducts from './components/ServiceProducts'

import useTemplatePageDetails from './hooks/useTemplatePageDetails'

const MODAL_KEY = 'TemplatePageDetailsModal'

type ModalCallbacks = {
  bulkUpdatePermissions: (payload: HandleCallbackActionParams) => Promise<object>,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const TemplatePageDetailsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    bulkUpdatePermissions,
    closeModal,
  } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { templatePage }: { templatePage: TemplatePageModel} = modalPayload

  const templatePagePayload = useTemplatePage(
    templatePage,
    {
      performHttpRequests: true,
      requestOptions: {
        include_template_page_service_products: true,
      },
    },
  )

  const {
    callbacks: {
      bulkUpdatePermissions: bulkUpdatePermissionsFn,
    },
    providers,
    templatePage: {
      id,
      permission_ids,
      permission_ids: { agencies, providers: permittedProviderIds },
      preview_image,
      title,
    },
  } = templatePagePayload

  const { groupedProductsByService } = useTemplatePageDetails({ templatePage })

  const { selectedAgency, selectedAgencyId } = useAgencySelector()
  // @ts-expect-error TS2345
  const isEnabledForAgency = agencies.includes(selectedAgencyId)

  const bulkUpdatePermissionsPayload = {
    callbacks: {
      action: bulkUpdatePermissionsFn,
    },
    entityParams: {
      agency_ids: toggleArray(permission_ids.agencies, selectedAgencyId),
    },
    toastText: 'Template Page Permissions Updated',
  }
  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Template" titleSecondLine="Details" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Template">
            <Box variant="whiteGrey" padding="medium">
              <Image
                backgroundSize="cover"
                height={240}
                placeholderUrl={placeholderImageUrl}
                url={preview_image}
              />
            </Box>
          </Form.Field>

          <Form.Field label={`Template #${id}`} marginTop="large">
            <Text fontSize="small" variant="ellipsis">{title}</Text>
          </Form.Field>

          <Form.Field label="Template Status" marginTop="large">
            <StatusBadge
              color={isEnabledForAgency ? 'green' : 'red'}
              text={isEnabledForAgency ? 'VISIBLE' : 'HIDDEN'}
            />
          </Form.Field>

          <ServiceProducts groupedProductsByService={groupedProductsByService} />

          <Providers permittedProviderIds={permittedProviderIds} providers={providers} />

          <Form.Field direction="column" label="More Options" marginTop="medium">
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={isEnabledForAgency ? faEyeSlash : faEye} />}
              // @ts-expect-error TS2345
              onClick={() => bulkUpdatePermissions(bulkUpdatePermissionsPayload)}
              size="medium"
              style={{ width: '100%' }}
            >
              {`${isEnabledForAgency ? 'Disable' : 'Enable'} for ${selectedAgency.short_name}`}
            </Button>
          </Form.Field>

        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <TemplatePageDetailsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
